/**
 * 图片懒加载工具
 */

// 图片懒加载观察器
let imageObserver = null

/**
 * 初始化图片懒加载
 */
export function initLazyLoad() {
  if ('IntersectionObserver' in window) {
    imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target
            const src = img.getAttribute('data-src')
            const srcset = img.getAttribute('data-srcset')
            
            if (src) {
              img.src = src
              img.removeAttribute('data-src')
            }
            
            if (srcset) {
              img.srcset = srcset
              img.removeAttribute('data-srcset')
            }
            
            img.classList.add('loaded')
            imageObserver.unobserve(img)
          }
        })
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.01
      }
    )
  }
}

/**
 * 观察图片元素
 * @param {HTMLElement} img - 图片元素
 */
export function observeImage(img) {
  if (imageObserver) {
    imageObserver.observe(img)
  } else {
    // 不支持 IntersectionObserver 时直接加载
    const src = img.getAttribute('data-src')
    const srcset = img.getAttribute('data-srcset')
    
    if (src) {
      img.src = src
      img.removeAttribute('data-src')
    }
    
    if (srcset) {
      img.srcset = srcset
      img.removeAttribute('data-srcset')
    }
  }
}

/**
 * 取消观察图片元素
 * @param {HTMLElement} img - 图片元素
 */
export function unobserveImage(img) {
  if (imageObserver) {
    imageObserver.unobserve(img)
  }
}

/**
 * 销毁懒加载观察器
 */
export function destroyLazyLoad() {
  if (imageObserver) {
    imageObserver.disconnect()
    imageObserver = null
  }
}

/**
 * Vue 指令: v-lazy
 * 使用方法: <img v-lazy="imageUrl" alt="...">
 */
export const vLazy = {
  mounted(el, binding) {
    el.setAttribute('data-src', binding.value)
    el.setAttribute('loading', 'lazy')
    observeImage(el)
  },
  updated(el, binding) {
    if (binding.value !== binding.oldValue) {
      el.setAttribute('data-src', binding.value)
      observeImage(el)
    }
  },
  unmounted(el) {
    unobserveImage(el)
  }
}

/**
 * 预加载关键图片
 * @param {Array<string>} urls - 图片URL数组
 */
export function preloadImages(urls) {
  return Promise.all(
    urls.map((url) => {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(url)
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`))
        img.src = url
      })
    })
  )
}

/**
 * 图片压缩 (移动端优化)
 * @param {File} file - 图片文件
 * @param {Object} options - 压缩选项
 * @returns {Promise<Blob>}
 */
export function compressImage(file, options = {}) {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.8,
    mimeType = 'image/jpeg'
  } = options

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      const img = new Image()
      
      img.onload = () => {
        let { width, height } = img
        
        // 计算缩放比例
        if (width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height
          height = maxHeight
        }
        
        // 创建 canvas
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        
        // 转换为 Blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Image compression failed'))
            }
          },
          mimeType,
          quality
        )
      }
      
      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = e.target.result
    }
    
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/**
 * 获取图片主色调
 * @param {string} url - 图片URL
 * @returns {Promise<string>} RGB颜色字符串
 */
export async function getImageDominantColor(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data
      
      let r = 0, g = 0, b = 0
      const pixelCount = data.length / 4
      
      for (let i = 0; i < data.length; i += 4) {
        r += data[i]
        g += data[i + 1]
        b += data[i + 2]
      }
      
      r = Math.round(r / pixelCount)
      g = Math.round(g / pixelCount)
      b = Math.round(b / pixelCount)
      
      resolve(`rgb(${r}, ${g}, ${b})`)
    }
    
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = url
  })
}

/**
 * 响应式图片URL生成器
 * @param {string} baseUrl - 基础URL
 * @param {number} width - 宽度
 * @returns {string}
 */
export function getResponsiveImageUrl(baseUrl, width) {
  // 这里可以根据你的图片CDN服务调整
  // 例如: 七牛云、阿里云OSS等都支持URL参数调整图片尺寸
  
  // 示例: 七牛云
  // return `${baseUrl}?imageView2/2/w/${width}`
  
  // 示例: 阿里云OSS
  // return `${baseUrl}?x-oss-process=image/resize,w_${width}`
  
  // 如果没有CDN支持,直接返回原URL
  return baseUrl
}

/**
 * 生成响应式图片 srcset
 * @param {string} baseUrl - 基础URL
 * @param {Array<number>} widths - 宽度数组
 * @returns {string}
 */
export function generateSrcSet(baseUrl, widths = [320, 640, 768, 1024, 1366, 1920]) {
  return widths
    .map(width => `${getResponsiveImageUrl(baseUrl, width)} ${width}w`)
    .join(', ')
}

